import { Box, IconButton, Typography, Stack, ButtonBase, styled } from "@mui/material"
import { t } from "i18next"
import Modifier from "./Modifier"
import ModifierStatusList from "./ModifierStatus"
import OrderBox from "./OrderBox"
import { useItemDetail } from "./ItemDetailContext"
import { LazyLoadImage } from "react-lazy-load-image-component"
import { useOrderStore } from "../../../store/order"
import { useInfoStore } from "../../../store/userInfo"
import RecommendView from "../Recommend/RecommandView"
import { useRef, useState, useMemo, useEffect, useCallback } from "react"
import { isAndroid } from "react-device-detect"
import { useMenuStore } from "../../../store/menuState"
import { useTranslation } from "react-i18next"
import { useWindowDimensions } from "../../../App"
import { logger } from "../../../utils/logger"
// import { useMenuCache } from "../../../store/menuCache"
import React, { memo } from 'react';
import { getCachedImage, setCachedImage, loadImageAsBase64 } from "../../../utils/imageCache";
import LazyPictureWithSkeleton from "../../../components/LazyPictureWithSkeleton";

const IMAGE_URL = process.env.REACT_APP_S3_IMAGE_PAPH;
const LazyPicture = styled(LazyLoadImage)`
  object-fit: cover;
  width: ${({ width }) => (width ? `${width}rem` : "100%")};
  height: ${({ width }) => (width ? `${width}rem` : "100%")};
  border-radius: 1.5rem;
`;

/**
 * MIME 타입 추출 함수
 */
const getMimeType = (imagePath: string): string => {
  const extension = imagePath.split(".").pop()?.toLowerCase();
  switch (extension) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "webp":
      return "image/webp";
    case "gif":
      return "image/gif";
    default:
      return "application/octet-stream"; // 기본 MIME 타입
  }
};

// ItemImage 컴포넌트 분리
const ItemImage = memo(({ itemDetail, imageSrc, t, storeId, currentLng }: { 
  itemDetail: any; 
  imageSrc: string; 
  t: any; 
  storeId: string | undefined; 
  currentLng: string 
}) => {
  if (!itemDetail?.images[0] || !storeId) {
    return (
      <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center'}}>
        <Box sx={{ width: itemDetail.hasModifier ? '407px': `644px`,  height: itemDetail.hasModifier ? '266px' : `362px`, borderRadius: '1.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#ccc'}}>
          <Typography sx={{fontSize: '3.0rem',  textAlign: 'left', fontWeight: '500', color: 'rgba(255, 255, 255, 0.80)',  letterSpacing: '-.03rem'}}>
            {storeId ? t(`${storeId}:itemNames.${itemDetail.itemId}.${currentLng}`) : ''}
          </Typography>
        </Box>
      </Box>
    );
  }
  
  return (
    <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center'}}>
      <Box sx={{ position: 'relative', width: itemDetail.hasModifier ? '407px': `644px`, height: itemDetail.hasModifier ? '266px' : `362px` }}>
        {/* 스켈레톤 배경 + 아이템 이름 */}
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          borderRadius: '1.5rem', 
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200% 100%',
          animation: 'loading 1.5s infinite',
          '@keyframes loading': {
            '0%': { backgroundPosition: '200% 0' },
            '100%': { backgroundPosition: '-200% 0' }
          },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1
        }}>
          <Typography sx={{fontSize: '3.0rem',  textAlign: 'left', fontWeight: '500', color: 'rgba(255, 255, 255, 0.80)',  letterSpacing: '-.03rem'}}>
            {storeId ? t(`${storeId}:itemNames.${itemDetail.itemId}.${currentLng}`) : ''}
          </Typography>
        </Box>
        {/* 실제 이미지 */}
        <img 
          style={{
            width: '100%', 
            height: '100%', 
            position: 'relative', 
            zIndex: 2,
            borderRadius: '1.5rem',
            objectFit: 'cover'
          }}
          src={imageSrc}
          alt="item"
        />
      </Box>
    </Box>
  );
});

// ModifierSection 컴포넌트 분리
const ModifierSection = memo(({ itemDetail, setItemDetail, setSelectRequired, checkedModifierPrice }: any) => {
  if (!itemDetail.hasModifier) return <Box />;
  
  return (
    <Modifier 
      itemDetail={itemDetail} 
      setItemDetail={setItemDetail} 
      setSelectRequired={setSelectRequired} 
      checkedModifierPrice={checkedModifierPrice} 
    />
  );
});

// OrderBoxSection 컴포넌트 분리
const OrderBoxSection = memo(({ itemDetail, checkLimitText, setSelectRequired, selectRequired }: any) => {
  if (!itemDetail.hasModifier) return null;
  
  return (
    <Box sx={{width: '33.0rem'}}>
      <OrderBox 
        setOpenOrderBox={false} 
        checkLimitText={checkLimitText} 
        setSelectRequired={setSelectRequired} 
        selectRequired={selectRequired}
      />
    </Box>
  );
});

// ItemDetails 컴포넌트 인터페이스 수정
const ItemDetails = memo(({ itemDetail, t, storeId, currentLng }: { 
  itemDetail: any; 
  t: any; 
  storeId: string | undefined; 
  currentLng: string 
}) => {
  if (!storeId) return null;

  if (itemDetail.hasModifier) {
    const itemName = t(`${storeId}:itemNames.${itemDetail.itemId}.${currentLng}`);
    const isLongTitle = itemName.length > 30; // 30자 이상이면 긴 제목으로 판단
    
    return (
      <Stack sx={{display: 'flex', width: '69.2rem', justifyContent: 'flex-start', alignItems: 'flex-start'}}>
        <Typography 
          id="item-title" 
          sx={{ 
            fontSize: isLongTitle ? '2.2rem' : '2.6rem', // 긴 제목이면 폰트 크기 줄임
            fontWeight: '600', 
            fontStyle:'normal', 
            letterSpacing: '-.024rem',
            maxHeight: '7.2rem',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            lineHeight: '1.2'
          }}
        >
          {itemName}
        </Typography>
        <Typography id="item-price" sx={{fontSize: itemDetail.isPriceVisible? '2.2rem': '1.8rem', fontWeight: '600', fontStyle:'normal', width: '16.7rem', height: '3.0rem', textAlign: 'left', my: '.4rem' }}>
          {itemDetail.isPriceVisible? `$${(itemDetail?.itemPrice/100).toFixed(2)}` : "Priced by add-ons"}
        </Typography>
        {itemDetail.enableDesc && (
          <Box sx={{width: '100%', height: '16.8rem', overflow:'overlay', overflowX: 'hidden', mt: '.5rem', mr: '.5rem', textWrap: 'wrap'}}>
            <Typography 
              component="div"
              sx={{
                fontSize: '2.0rem', 
                whiteSpace: 'break-spaces', 
                fontWeight: '400', 
                fontStyle: 'normal', 
                textWrap: 'wrap'
              }}
            >
              {t(`${storeId}:itemDescs.${itemDetail.itemId}.${currentLng}`)}
            </Typography>
          </Box>
        )}
      </Stack>
    );
  }

  return (
    <Stack sx={{width: '50.5rem', justifyContent: 'flex-end', alignItems: 'flex-start'}}>
      <Typography id="item-title" sx={{ fontSize: '3.0rem', fontWeight: '600', fontStyle:'normal', lineHeight: '3.0rem', letterSpacing: '-.024rem', width: '50.5rem', maxHeight: '7.2rem'}}>
        {t(`${storeId}:itemNames.${itemDetail.itemId}.${currentLng}`)}
      </Typography>
      <Typography id="item-price" sx={{fontSize: itemDetail.isPriceVisible? '2.2rem': '1.8rem', fontWeight: '600', fontStyle:'normal', width: '8.3rem', height: '3.0rem', textAlign: 'left', my: '.4rem' }}>
        {itemDetail.isPriceVisible? `$${(itemDetail?.itemPrice/100).toFixed(2)}` : "Priced by add-ons"}
      </Typography>
    </Stack>
  );
});

const QuantityControl = memo(({ itemDetail, handleSubCount, handleAddCount, orderTotalPrice, handleAddtoCart, checkLimitCountText }: any) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const lastClickTime = useRef(0);
  const isProcessing = useRef(false);
  const MIN_CLICK_INTERVAL = 50;
  // logger.info({ event: 'button_click', button: 'add to cart' }, 'add to cart 버튼 클릭');
  const isClickAllowed = useCallback(() => {
    const now = Date.now();
    if (now - lastClickTime.current < MIN_CLICK_INTERVAL || isProcessing.current) {
      return false;
    }
    lastClickTime.current = now;
    return true;
  }, []);

  const optimizedHandleSubCount = useCallback(() => {
    if (!isClickAllowed() || isUpdating) return;
    isProcessing.current = true;
    requestAnimationFrame(() => {
      setIsUpdating(true);
      handleSubCount();
      setIsUpdating(false);
      isProcessing.current = false;
    });
  }, [handleSubCount, isUpdating]);

  const optimizedHandleAddCount = useCallback(() => {
    if (!isClickAllowed() || isUpdating) return;
    isProcessing.current = true;
    requestAnimationFrame(() => {
      setIsUpdating(true);
      handleAddCount();
      setIsUpdating(false);
      isProcessing.current = false;
    });
  }, [handleAddCount, isUpdating]);

  return (
    <Box sx={{position: 'fixed', bottom: '0rem', width: '100%'}}>
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'flex-start', width: '100%', borderRadius: 0, pt: '3.0rem', pb: '3.0rem', gap: '2.0rem' }}>
        {(itemDetail.maxAllowed || itemDetail.minRequired! > 1) &&
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', mr: '1.0rem', alignItems: 'center', height: '5.5rem' }}>
            <img style={{width: '3.9rem', height: '3.9rem'}}
              src={process.env.PUBLIC_URL+'/assets/images/warning_r.svg'}
              alt="warning"
            />
            <Typography sx={{fontSize: '2.6rem', fontWeight: '600', color: '#F72E13'}}>{checkLimitCountText()}</Typography>
          </Box>
        }
        <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', width:'17.2rem', height:'5.5rem',
                padding: '.4rem 1.4rem', borderRadius: '5.5rem', backgroundColor: '#fff', border: '2px solid #E9E9E9', gap: '2.0rem'}}>
          <div className="touch-area" onClick={optimizedHandleSubCount}>
            <IconButton sx={{p : '0rem'}} aria-label="minus">
              <LazyPicture sx={{background: '#fff', width: '2.6rem' , height: '2.6rem'}}
                src={process.env.PUBLIC_URL+'/assets/images/minus.svg'}
                alt="minus"
              />
            </IconButton>
          </div>
          <Typography id="item-price" sx={{fontSize: '2.2rem', fontWeight: '700', fontStyle: 'normal', width: '6.0rem', lineHeight: 'normal', textAlign: 'center'}}>
            {itemDetail.quantity}
          </Typography>
          <div className="touch-area" onClick={optimizedHandleAddCount}>
            <IconButton sx={{p : '0rem'}} aria-label="plus">
              <LazyPicture sx={{background: '#fff', width: '2.6rem' , height: '2.6rem'}}
                src={process.env.PUBLIC_URL+'/assets/images/add.svg'}
                alt="add"
              />
            </IconButton>
          </div>
        </Box>
        <div
          style={{
            display: 'inline-block',
            padding: '20px',
            margin: '-20px 0px -20px 50px',
            borderRadius: '999px',
          }}
          onClick={(e) => handleAddtoCart(e, itemDetail)}
        >
          <ButtonBase
            sx={{
              width: '32.0rem',
              padding: '.6rem 2.0rem',
              justifyContent: 'center',
              height: '5.5rem',
              backgroundColor: '#DA291C',
              borderRadius: '5.5rem',
              pointerEvents: 'none',
            }}
          >
            <Box sx={{pointerEvents: 'auto', display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center'}}>
              <Typography sx={{fontSize: '20px', fontWeight: '600', fontStyle: 'normal', letterSpacing: '-.22px', textTransform:' none', width: 'auto', flex: '0 0 auto', flexWrap: 'nowrap', color: '#fff', textAlign: 'center', textWrap: 'noWrap'}}>Add to Cart</Typography>
              <Typography sx={{fontSize: '20px', fontWeight: '600', fontStyle: 'normal', letterSpacing: '-.22px', textTransform:' none', width: 'auto', color: '#fff', textAlign: 'center', ml: '1.0rem'}}>${(itemDetail.quantity * orderTotalPrice/100).toFixed(2)}</Typography>
            </Box>
          </ButtonBase>
        </div>
      </Box>
    </Box>
  );
});

const LandscapeDetail = () => {
  const { itemDetail, selectRequired, setSelectRequired, setItemDetail, handleClose, handleAddCount, handleSubCount, handleAddtoCart, checkLimitCountText } = useItemDetail();
  const { userInfo, currentLng } = useInfoStore();
  const storeId = userInfo?.storeId?.toString();
  const {t} =  useTranslation(`${storeId}`)
  const { menuDao } = useMenuStore()
  const { height } = useWindowDimensions()
  const { preOrder, orderTotalPrice, checkedModifierPrice } = useOrderStore();
  // const menuCache = useMenuCache((state) => state.menuCache);
  
  // 네이티브 이미지 캐시 관련 ref 및 강제 리렌더링용 상태
  const base64CacheRef = useRef(new Map<string, string>());
  const [nativeImageVersion, setNativeImageVersion] = useState(0);
  const [imageSrc, setImageSrc] = useState('')
      /**
   * menuDao를 ID 기준으로 매핑
   */
  const menuMap = useMemo(() => {
    return new Map(menuDao.map((menu: any) => [menu.id, menu]));
  }, [menuDao]);

  let menuItem = null;
  let webImagePath = ''
  let androidImagePath = ''
  const TRANSPARENT_IMAGE = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";
  const defaultImage = "/assets/images/no_image.png";
  const getBase64ImageSrc = useCallback((imagePath: string | null): string => {
    if (!imagePath) return defaultImage;
    
    // 전역 캐시에서 확인
    const cached = getCachedImage(imagePath);
    if (cached) {
      return cached;
    }
    
    // 로컬 캐시에서 확인
    if (base64CacheRef.current.has(imagePath)) {
      const localCached = base64CacheRef.current.get(imagePath)!;
      // 전역 캐시에도 저장
      setCachedImage(imagePath, localCached);
      return localCached;
    }

    // 동기적으로 네이티브 API 시도 (즉시 반환)
    if (isAndroid && window.Android?.getImageAsBase64) {
      try {
        const base64Image = window.Android.getImageAsBase64(imagePath);
        if (base64Image) {
          const mimeType = getMimeType(imagePath);
          const src = `data:${mimeType};base64,${base64Image}`;
          base64CacheRef.current.set(imagePath, src);
          setCachedImage(imagePath, src);
          return src; // 즉시 반환
        }
      } catch (error) {
        logger.warn(`Failed to load image synchronously: ${imagePath}`, error);
      }
    }

    // 동기 로드 실패 시 비동기로 재시도
    requestAnimationFrame(() => {
      if (isAndroid && window.Android?.getImageAsBase64) {
        try {
          const base64Image = window.Android.getImageAsBase64(imagePath);
          if (base64Image) {
            const mimeType = getMimeType(imagePath);
            const src = `data:${mimeType};base64,${base64Image}`;
            base64CacheRef.current.set(imagePath, src);
            setCachedImage(imagePath, src);
            setNativeImageVersion(v => v + 1);
          }
        } catch (error) {
          logger.warn(`Failed to load image asynchronously: ${imagePath}`, error);
        }
      }
    });
    return TRANSPARENT_IMAGE;
  }, []);

  useEffect(() => {
    if (itemDetail) {
      const menuItem = menuMap.get(itemDetail.itemId);
      const webImagePath = IMAGE_URL + `/${itemDetail.images[0]}`;
      const androidImagePath = menuItem?.images?.[0] ?? null;
      const src = isAndroid
        ? (androidImagePath ? getBase64ImageSrc(androidImagePath) : webImagePath)
        : webImagePath;
      setImageSrc(src);
    }
  }, [itemDetail, nativeImageVersion, menuMap, getBase64ImageSrc]);

  // OrderBox props 메모이제이션
  const orderBoxProps = useMemo(() => ({
    setOpenOrderBox: false,
    checkLimitText: checkLimitCountText,
    setSelectRequired,
    selectRequired
  }), [checkLimitCountText, setSelectRequired, selectRequired]);

  if(!itemDetail || !storeId) return null;

  return (
    <Box className="dialog-content" onClick={(e) => e.stopPropagation()}>
      <Box sx={{background: '#fff', flexDirection: 'row', width: '100%', height: '6.8rem', borderRadius: '1.5rem 1.5rem'}}>
        <div className="touch-area" onClick={handleClose}>
          <IconButton aria-label="close" style={{position: 'absolute', right: '3.0rem', top: '0rem', width: '6.6rem', height: '6.6rem', display: 'flex', justifyContent: 'center', alignItems: 'center'}} >
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
              <img style={{position: 'absolute',top: 0, left: 0, width: '100%', height: '100%', }}
                src={process.env.PUBLIC_URL+'/assets/images/close_bg.svg'}
                alt="close"
              />
              <img style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '2.0rem', height: '2.0rem',}}
                src={process.env.PUBLIC_URL+'/assets/images/close_w.svg'}
                alt="close"
              />
            </div>
          </IconButton>
        </div>
      </Box>
      <Box sx={{width: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflow: 'hidden'}}>
        {itemDetail.hasModifier && (
          <Box sx={{width: '18.0rem'}}>
            <ModifierStatusList itemDetail={itemDetail} setItemDetail={setItemDetail} />
          </Box>
        )}
        <Box sx={{flex: 1, p: itemDetail.hasModifier? '0rem 1.0rem 0rem 2.0rem': '0rem 5.0rem 1.0rem 5.0rem', overflow: 'hidden'}}>
          <Box sx={{ height: `calc(${height/10}rem - ${itemDetail.hasModifier ? 4.9: 8.9}rem)`, mt: '1.0rem'}}>
            <Box sx={{overflow: 'auto', overflowX: 'hidden', pr: '1.0rem', maxHeight: 'calc(100% - 10.0rem)' }}>
              <Box sx={{display: 'flex', flexDirection: 'row', gap: '2.0rem', mb: '2.0rem'}}>
                <ItemImage 
                  itemDetail={itemDetail} 
                  imageSrc={imageSrc} 
                  t={t}
                  storeId={storeId}
                  currentLng={currentLng}
                />
                <ItemDetails 
                  itemDetail={itemDetail}
                  t={t}
                  storeId={storeId}
                  currentLng={currentLng}
                />
              </Box>
              {itemDetail.hasModifier ? (
                <Modifier 
                  itemDetail={itemDetail} 
                  setItemDetail={setItemDetail} 
                  setSelectRequired={setSelectRequired} 
                  checkedModifierPrice={checkedModifierPrice}
                />
              ) : (
                itemDetail.enableDesc && (
                  <Box sx={{width: '100%', height: '16.8rem', overflow:'overlay', overflowX: 'hidden', mt: '.5rem'}}>
                    <Typography 
                      component="div"
                      sx={{
                        fontSize: '2.2rem', 
                        whiteSpace: 'pre-line', 
                        fontWeight: '400', 
                        fontStyle: 'normal'
                      }}
                    >
                      {t(`${storeId}:itemDescs.${itemDetail.itemId}.${currentLng}`)}
                    </Typography>
                  </Box>
                )
              )}
              {itemDetail.hasRecommend && <RecommendView itemDetail={itemDetail} />}
            </Box>
          </Box>
          {!itemDetail.hasModifier && (
            itemDetail.useAgeVerification && itemDetail.requiresAgeVerification && !itemDetail.isAgeVerified ? (
              <Box sx={{position: 'fixed', bottom: '0rem', width: '100%'}}>
                <Box sx={{display: 'flex', flexDirection:'row', justifyContent: 'center', alignItems: 'flex-start', background: '#fff' , width: '100%', borderRadius: 0, mt: '3.0rem', mb: '3.0rem',  height: '5.5rem'}}>
                  {/* <img style={{marginRight: '1.3rem', marginLeft: '.4rem', width: '4.0rem', height: '4.0rem'}}
                    src={process.env.PUBLIC_URL+'/assets/images/21+.svg'}
                    alt="cart"
                  />  */}
                  <Typography sx={{fontSize: '2.4rem', fontWeight:'600'}}>Please ask your server to order this item.</Typography>
                </Box>
              </Box>
            ) : (
              <QuantityControl 
                itemDetail={itemDetail}
                handleSubCount={handleSubCount}
                handleAddCount={handleAddCount}
                orderTotalPrice={orderTotalPrice}
                handleAddtoCart={handleAddtoCart}
                checkLimitCountText={checkLimitCountText}
              />
            )
          )}
        </Box>
        {itemDetail.hasModifier && (
          <Box sx={{width: '33.0rem', height: '100%'}}>
            <OrderBox {...orderBoxProps} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LandscapeDetail